"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export interface ModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback to close the modal */
    onClose: () => void;
    /** Modal content */
    children: React.ReactNode;
    /** Hide the built-in floating close button (for callers that render their own) */
    hideCloseButton?: boolean;
}

/**
 * Modal - A minimal overlay modal.
 *
 * Rendered via `createPortal` so events never bubble into the mount site's DOM tree
 * (critical for modals opened from inside `<Link>` or other interactive wrappers).
 *
 * Features:
 * - Backdrop with click-to-close
 * - ESC key to close
 * - Scroll lock when open
 * - Close button (X) in top right
 *
 * @example
 * ```tsx
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
 *   <MyContent />
 * </Modal>
 * ```
 */
export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, hideCloseButton = false }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);

    // Lock body scroll when modal is open, compensating for scrollbar width to prevent layout shift
    useEffect(() => {
        if (isOpen) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        } else {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        }

        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        };
    }, [isOpen]);

    // Close on ESC key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen || !mounted) return null;

    return createPortal(
        // Stop React synthetic events from bubbling to the opener's React tree.
        // The portal detaches the DOM, but React synthetic events bubble through the
        // component tree (not the DOM tree) — so a modal opened from inside a <Link>
        // would still trigger navigation on clicks inside the modal without this guard.
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />

            {/* Content */}
            <div className="relative" role="dialog" aria-modal="true">
                {/* Close button */}
                {!hideCloseButton && (
                    <button
                        onClick={onClose}
                        className="absolute -top-3 -right-3 z-10 cursor-pointer p-1.5 rounded-full bg-white text-gray-600 hover:text-gray-900 shadow-lg transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                )}

                {children}
            </div>
        </div>,
        document.body,
    );
};
